<template>
  <div class="owc-media-gallery-thumbnails">
    <div class="owc-media-gallery-thumbnails__inner" ref="inner">
      <div class="owc-media-gallery-thumbnails__grid" ref="grid">
        <div
          v-for="(item, index) in thumbnails"
          :key="index"
          :class="[
            'owc-media-gallery-thumbnails__grid-item',
            {
              'owc-media-gallery-thumbnails__grid-item--overflowing': indexOfOverflingItem === index,
            },
            {
              'owc-media-gallery-thumbnails__grid-item--active': value === index,
            },
          ]"
          @click="selectItem(index)"
        >
          <GenericImage :component-id="getThumbnailComponentId(item)"></GenericImage>
          <div v-if="isCarouselItemAVideo(item)" class="owc-media-gallery-thumbnails__video-overlay">
            <wb-icon name="play" class="owc-media-gallery-thumbnails__video-play-icon"></wb-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { MediaGalleryCarouselImage, MediaGalleryCarouselItem } from './MediaGalleryOverlay';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import { THUMBNAILS_TOTAL_DISPLAY_AMOUNT, THUMBNAILS_THROTTLE_LIMIT } from './MediaGalleryOverlay.config';
import { isCarouselItemAVideo, transformCarouselToUnifiedFormat } from './MediaGalleryOverlayUtils';
export default Vue.extend({
  name: 'owc-media-gallery-carousel-thumbnails',
  mixins: [
    createEventListener('resize', 'updateVisibleItems', {
      throttleLimit: THUMBNAILS_THROTTLE_LIMIT,
    }),
  ],
  components: {
    GenericImage,
  },
  props: {
    thumbnails: {
      type: Array as () => MediaGalleryCarouselItem<MediaGalleryCarouselImage>[],
      required: true,
    },
    value: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    itemCount(): number {
      return this.thumbnails.length;
    },
    isThumbnailsScrollable(): boolean {
      return this.itemCount > THUMBNAILS_TOTAL_DISPLAY_AMOUNT;
    },
    indexOfOverflingItem(): number {
      if (!this.isThumbnailsScrollable) {
        return -1;
      }
      const firstItemInRow = this.itemCount - THUMBNAILS_TOTAL_DISPLAY_AMOUNT;
      const lastItemInRow = THUMBNAILS_TOTAL_DISPLAY_AMOUNT - 1;
      return this.overflowingLeft ? firstItemInRow : lastItemInRow;
    },
  },
  watch: {
    value() {
      // update thumbnails when the value over v-model changed
      this.updateVisibleItems();
    },
  },
  data: () => ({
    overflowingLeft: false,
  }),
  mounted() {
    this.updateVisibleItems();
  },
  methods: {
    getThumbnailComponentId(item: MediaGalleryCarouselItem<MediaGalleryCarouselImage>): string {
      return this.isCarouselItemAVideo(item) ? `${item.id}-thumbnail` : item.id;
    },
    isCarouselItemAVideo,
    selectItem(index: number): void {
      this.updateVisibleItems();
      this.$emit('updateCurrentItem', index + 1);
    },
    updateVisibleItems(): void {
      const innerElement = this.$refs.inner as Element;
      const gridElement = this.$refs.grid as HTMLElement;
      let difference = 0;

      // move thumbnails to left then the currently active element
      // passed the center
      this.overflowingLeft = this.value + 1 > this.itemCount / 2;

      if (this.overflowingLeft && this.isThumbnailsScrollable) {
        difference = innerElement.clientWidth - gridElement.clientWidth;
      }

      gridElement.style.setProperty('--grid-left-offset', String(difference));
    },
    getThumbnailImageSource(item: MediaGalleryCarouselItem<MediaGalleryCarouselImage>): string {
      return transformCarouselToUnifiedFormat(item).src;
    },
  },
});
</script>
<style lang="scss">
@import '@owc-styles/utils/z-index-list';

%base-overlay {
  position: absolute;
  top: 0;
  left: 0;

  z-index: $z-index-default;

  box-sizing: border-box;

  width: 100%;
  height: 100%;

  content: '';
}
.owc-media-gallery-thumbnails {
  $thumbnails-max-item-count: 7;
  $thumbnails-displayed-amount: 6;
  $thumbnails-transition-duration: 0.27s;
  $thumbnails-overlay-opacity: 0.4;
  $thumbnails-active-item-border-width: 2px;

  display: none;
  margin-top: var(--wb-spacing-s);
  margin-bottom: var(--wb-spacing-xs);

  @include breakpoint-from(mq4) {
    display: block;
  }

  &__inner {
    position: relative;

    overflow: hidden;
  }
  &__grid {
    --grid-left-offset: 0;

    display: grid;
    grid-template-columns: repeat($thumbnails-max-item-count, 1fr);

    /* Since the total item count the gallery can have is 7
     * and the max ammount of display items are 6 an overflow of one
     * item is necessary.
     * |----------------------------| < wrapper component
     * |=)].[=)].[=)].[=)].[=)].[=)]|[=)]
     * |----------------------------| ^ overflowing item
     * thumbnail: [=)]
     * spaicing: .
     * The overflow is achived by adding 1/6 of the total with (plus spacing)
     * to the total with.
     * Why 7 / 6?
     * 6/6 + 1/6 = 7/6
     */
    width: 100% * math.div($thumbnails-max-item-count, $thumbnails-displayed-amount);

    column-gap: var(--wb-spacing-s);

    transform: translateX(calc(var(--grid-left-offset) * 1px));

    transition: transform $wb-move $thumbnails-transition-duration;
  }
  &__grid-item {
    position: relative;

    cursor: pointer;
    opacity: 0.5;

    transition: opacity $wb-fade 0.27s;

    &:hover {
      opacity: 1;
    }
  }
  &__grid-item--active {
    opacity: 1;

    &::after {
      @extend %base-overlay;

      border: solid $thumbnails-active-item-border-width var(--wb-blue-45);
    }
  }
  &__grid-item--overflowing::before {
    @extend %base-overlay;

    @include wb-type-heading-s;

    display: flex;

    align-items: center;
    justify-content: center;

    color: var(--wb-white);

    background-color: rgba($wb-black, $thumbnails-overlay-opacity);

    content: '+1';
  }
  &__video-overlay {
    position: absolute;

    top: 0;
    left: 0;

    display: flex;

    align-items: center;
    justify-content: center;

    width: 100%;
    height: 100%;
  }
  &__video-play-icon {
    z-index: $z-index-default;

    display: flex;

    align-items: center;
    justify-content: center;

    width: 8px;

    color: var(--wb-black);

    &::before {
      position: absolute;

      z-index: $z-index-negative;

      width: 40px;
      height: 40px;

      border-radius: 50%;

      background-color: var(--wb-white);

      box-shadow: 0px 2px 4px 0px;

      content: '';
    }
  }
}
</style>
